import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { IntakeField } from '@/api/firstConsultations/firstConsultationsApi.types';
import { useReplaceConsultationIntakeFieldsMutation } from '@/hooks/useConsultationConfig';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Icon } from '@/components/common/Icon';
import { FieldError } from '@/components/ui/fieldError';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

const INTAKE_FIELD_SECTIONS = ['history', 'lifestyle', 'assessment'];
const INTAKE_FIELD_TYPES = ['text', 'textarea', 'boolean'];

interface EditableIntakeField {
  key: string;
  code: string | null;
  section: string;
  labelEn: string;
  labelPl: string;
  fieldType: string;
}

let intakeFieldKeyCounter = 0;
function nextIntakeFieldKey(): string {
  intakeFieldKeyCounter = intakeFieldKeyCounter + 1;
  return 'intake-field-' + intakeFieldKeyCounter;
}

function toRows(fields: IntakeField[]): EditableIntakeField[] {
  return fields.map(function toRow(field) {
    return {
      key: nextIntakeFieldKey(),
      code: field.code,
      section: field.section,
      labelEn: field.labelEn,
      labelPl: field.labelPl,
      fieldType: field.fieldType,
    };
  });
}

interface IntakeFieldsEditorProps {
  initialFields: IntakeField[];
}

export function IntakeFieldsEditor({ initialFields }: IntakeFieldsEditorProps) {
  const { t } = useTranslation('consultations');
  const mutation = useReplaceConsultationIntakeFieldsMutation();
  const [rows, setRows] = useState<EditableIntakeField[]>(toRows(initialFields));
  const [isSaving, setIsSaving] = useState(false);
  const [hasIncompleteRows, setHasIncompleteRows] = useState(false);

  useEffect(
    function syncFromServer() {
      setRows(toRows(initialFields));
      setHasIncompleteRows(false);
    },
    [initialFields]
  );

  function updateRow(key: string, patch: Partial<EditableIntakeField>) {
    setHasIncompleteRows(false);
    setRows(function apply(previous) {
      return previous.map(function mapRow(row) {
        return row.key === key ? { ...row, ...patch } : row;
      });
    });
  }

  function removeRow(key: string) {
    setRows(function apply(previous) {
      return previous.filter(function keep(row) {
        return row.key !== key;
      });
    });
  }

  function addRow(section: string) {
    setRows(function apply(previous) {
      return [
        ...previous,
        { key: nextIntakeFieldKey(), code: null, section, labelEn: '', labelPl: '', fieldType: 'text' },
      ];
    });
  }

  async function save() {
    const hasIncomplete = rows.some(function incomplete(row) {
      return isBlank(row.labelEn) || isBlank(row.labelPl);
    });
    if (hasIncomplete) {
      setHasIncompleteRows(true);
      showErrorNotification(t('settings.incompleteRow'));
      return;
    }

    const drafts = rows.map(function toDraft(row) {
      return {
        code: row.code,
        section: row.section,
        labelEn: row.labelEn.trim(),
        labelPl: row.labelPl.trim(),
        fieldType: row.fieldType,
      };
    });
    setIsSaving(true);
    try {
      const saved = await mutation.mutateAsync(drafts);
      setRows(toRows(saved));
      setHasIncompleteRows(false);
      showSuccessNotification(t('settings.saved'));
    } catch (error) {
      const message = resolveApiErrorMessage(error);
      showErrorNotification(message.length > 0 ? message : t('settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card padding="md">
      <h2 className="text-sm font-bold text-zooindigo mb-4">{t('settings.intakeFieldsTitle')}</h2>

      <div className="space-y-6">
        {INTAKE_FIELD_SECTIONS.map(function renderSection(section) {
          const sectionRows = rows.filter(function inSection(row) {
            return row.section === section;
          });
          return (
            <div key={section}>
              <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-2">
                {t('editor.section.' + section)}
              </p>
              {sectionRows.length === 0 ? (
                <p className="text-sm text-zoomuted mb-2">{t('settings.empty')}</p>
              ) : (
                <div className="space-y-2.5 mb-2">
                  {sectionRows.map(function renderRow(row) {
                    return (
                      <div
                        key={row.key}
                        className="flex flex-col sm:grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2.5 sm:gap-2 sm:items-center border-b border-zooborder pb-3 last:border-b-0 last:pb-0 sm:border-b-0 sm:pb-0"
                      >
                        <Input
                          value={row.labelEn}
                          placeholder={t('settings.labelEn')}
                          hasError={hasIncompleteRows && isBlank(row.labelEn)}
                          onChange={function update(changeEvent) {
                            updateRow(row.key, { labelEn: changeEvent.target.value });
                          }}
                        />
                        <Input
                          value={row.labelPl}
                          placeholder={t('settings.labelPl')}
                          hasError={hasIncompleteRows && isBlank(row.labelPl)}
                          onChange={function update(changeEvent) {
                            updateRow(row.key, { labelPl: changeEvent.target.value });
                          }}
                        />
                        <SearchableSelect
                          inputId={'intake-field-type-' + row.key}
                          ariaLabel={t('settings.fieldType')}
                          value={row.fieldType}
                          isDisabled={isSaving}
                          options={INTAKE_FIELD_TYPES.map(function toOption(fieldType) {
                            return { value: fieldType, label: t('settings.fieldTypeOption.' + fieldType) };
                          })}
                          onChange={function pick(nextValue) {
                            updateRow(row.key, { fieldType: nextValue });
                          }}
                        />
                        <Button
                          variant="destructiveGhost"
                          size="icon"
                          aria-label={t('settings.removeRow')}
                          onClick={function remove() {
                            removeRow(row.key);
                          }}
                          className="self-start sm:self-auto w-8 h-8"
                        >
                          <Icon name="Trash" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="small"
                disabled={isSaving}
                onClick={function add() {
                  addRow(section);
                }}
              >
                <Icon name="Plus" /> {t('settings.addField')}
              </Button>
            </div>
          );
        })}
      </div>

      {hasIncompleteRows ? <FieldError messageKey="consultations:settings.incompleteRow" /> : null}

      <div className="flex justify-end mt-4">
        <Button type="button" isLoading={isSaving} onClick={save}>
          {t('settings.save')}
        </Button>
      </div>
    </Card>
  );
}
